/// <reference path="../../../calculator.d.ts" />
import { createSelector } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { dayjs } from 'src/app/core/dayjs/dayjs';

import { encodeMetadataAccess } from 'src/app/job/job-locations-helpers';
import { overviewLocationsFeature } from 'src/app/job/job-overview/overview-locations/overview-locations.feature';
import { selectOrderedLocations } from 'src/app/job/job-overview/overview-locations/overview-locations.selectors';
import { overviewTimelineFeature } from 'src/app/job/job-overview/overview-timeline/overview-timeline.feature';
import { selectTimelineRange } from 'src/app/job/job-overview/overview-timeline/overview-timeline.selectors';

import { BaseFieldFragment, BaseZoneWithParentFragment } from '../../generated/graphql.generated';
import { zonesFeature } from '../core/zones/zones.reducer';
import { getFieldValue, splitName } from '../fields/fields.utils';
import { JOB_ROLE_MAP } from '../global.constants';
import { brandingFeature } from '../state/branding.store';
import { generateBranches } from '../utilities/zones.util';

import { getDisabledStatus } from './job-charges-helpers';
import { selectInventoryTotals } from './job-inventory/job-inventory.selectors';
import {
  selectJob,
  selectJobEvents,
  selectJobLocations
} from './job-tool.reducer';

export const selectJobLocationsWithChanges = createSelector(
  selectJobLocations,
  overviewLocationsFeature.selectLocationEditors,
  (jobLocations, locationEditors) => {

    const jobLocationWithChanges = cloneDeep(jobLocations);

    for (const locationType of [ 'start', 'end' ]) {

      const locationEditor = locationEditors[locationType];

      if (!locationEditor) continue;

      const jobLocation: DetailedLocation = jobLocationWithChanges[locationType];

      if (!jobLocation) continue;

      jobLocation.addressString = locationEditor.address;
      jobLocation.dwellingType = locationEditor.buildingType;
      jobLocation.size = locationEditor.size;
      jobLocation.access = encodeMetadataAccess(locationEditor);
    }

    return jobLocationWithChanges;
  }
);

export const selectJobDistances = createSelector(
  selectOrderedLocations,
  overviewLocationsFeature.selectCalculateDistancesOutput,
  (orderedJobLocations, calculateDistancesOutput) => {

    const distances: { [ key: string ]: Distance } = {};

    if (!calculateDistancesOutput?.distances) return distances;

    for (let i = 0; i < calculateDistancesOutput.distances.length; i++) {

      const start = orderedJobLocations[i]?.locationType;

      const end = orderedJobLocations[i + 1]?.locationType;

      if (!start || !end) continue;

      const key = `${start}_${end}`;

      distances[key] = calculateDistancesOutput.distances[i];
    }

    return distances;
  },
);

export const selectJobFields = createSelector(
  selectJob,
  (job) => {

    const fieldsMap: Record<string, Record<string, any>> = {};

    if (!job) return fieldsMap;

    job?.fields?.forEach((field) => {
      const { namespace, key } = splitName(field as BaseFieldFragment);
      if (!fieldsMap[namespace]) fieldsMap[namespace] = {};
      fieldsMap[namespace][key] = getFieldValue(field);
    });

    return fieldsMap;
  }
);

/**
 * Resolves the dynamic pricing tier for the first date in the job's timeline,
 * using data already fetched by the viewDates query (stored in overviewTimelineFeature).
 *
 * Returns a DynamicPricingInfo-shaped object if a tier is available, or undefined if
 * the forecast data has not been loaded or no tier is available for that date.
 */
export const selectDynamicPricingForTimeline = createSelector(
  selectJob,
  selectTimelineRange,
  overviewTimelineFeature.selectJobV3OverviewTimelineState,
  (job, timelineRange, timelineState: any) => {
    const viewDatesMap: Record<string, any> = timelineState?.viewDatesMap ?? {};

    // Use the first date from the timeline range as the primary pricing date
    const targetDate: string | undefined = timelineRange?.[0] ?? (job as any)?.timeline;
    if (!targetDate) { return undefined; }

    const dateStr = dayjs(targetDate).isValid() ? dayjs(targetDate).format('YYYY-MM-DD') : targetDate;

    // Look up the viewDates entry for this date
    const viewDateEntry = viewDatesMap[dateStr];
    const tier = viewDateEntry?.prophet?.discount?.tier;

    if (!tier || typeof tier.name !== 'string' || typeof tier.percent !== 'number') {
      return undefined;
    }

    return {
      date: dateStr,
      tierName: tier.name as string,
      tierPercent: tier.percent as number,
      tierColor: (tier.color as string | undefined) ?? undefined,
    };
  },
);

// Requirement: when this selector is changed we ned duplicate changes in the mapJobToCalculatorData function
// on the backend, so backend eventually will be able to run calculator
export const selectJobDataForCalculator = createSelector(
  selectJob,
  selectJobDistances,
  selectJobLocationsWithChanges,
  selectJobFields,
  selectInventoryTotals,
  selectJobEvents,
  selectDynamicPricingForTimeline,
  (job, distances, locations, fields, inventoryTotals, events, dynamicPricing) => {

    const disabledStatus = getDisabledStatus(undefined, job);
    const startEnd = distances.start_end;

    const dockStart = distances.dock_start;
    const endDock = distances.end_dock;

    const timezone = job?.zone?.timezone;
    const parsedTimeline = job?.timeline
      ? (timezone ? dayjs.tz(job.timeline, timezone) : dayjs(job.timeline))
      : null;
    const month = parsedTimeline?.isValid() ? parsedTimeline.format('MMM') : null;
    const date = parsedTimeline?.isValid() ? parsedTimeline.date() : null;

    const dockStartTotalDistance = dockStart?.distance ?? job?.distances?.dock_start?.totalDistance ?? 0;
    const endDockTotalDistance = endDock?.distance ?? job?.distances?.end_dock?.totalDistance ?? 0;
    const startEndTotalDistance = startEnd?.distance ?? job?.distances?.start_end?.totalDistance ?? 0;

    const totalDistanceIncludingDock = dockStartTotalDistance + startEndTotalDistance + endDockTotalDistance;

    const dockStartEstimatedTime = dockStart?.estimatedTime ?? job?.distances?.dock_start?.estimatedTime ?? 0;
    const endDockEstimatedTime = endDock?.estimatedTime ?? job?.distances?.end_dock?.estimatedTime ?? 0;
    const startEndEstimatedTime = startEnd?.estimatedTime ?? job?.distances?.start_end?.estimatedTime ?? 0;

    const totalEstimatedTimeIncludingDock = dockStartEstimatedTime + startEndEstimatedTime + endDockEstimatedTime;

    // Requirement: must stay in parity with mapJobToCalculatorData in the backend (see the note
    // there). Both clamp on read: a plain `|| 0` rejects NaN but passes a negative total and passes
    // Infinity, and this becomes the calculator's default `weight` — which drives man-hours, truck
    // count and price.
    const totalWeight = Number(inventoryTotals.totalWeight);

    const data: JobData = {
      totalWeight: Number.isFinite(totalWeight) && totalWeight >= 0 ? totalWeight : 0,
      totalDistance: startEndTotalDistance,
      totalDistanceIncludingDock,
      averageTime: startEnd?.averageTime ?? job?.distances?.start_end?.averageTime ?? 0,
      distances: {
        ...(job?.distances || {}),
        start_end: {
          ...(job?.distances?.start_end || {}),
          totalDistance: startEnd?.distance ?? job?.distances?.start_end?.totalDistance,
          estimatedTime: startEnd?.estimatedTime ?? job?.distances?.start_end?.estimatedTime,
          averageTime: startEnd?.averageTime ?? job?.distances?.start_end?.averageTime,
          distanceUnits: startEnd?.distanceUnits ?? job?.distances?.start_end?.distanceUnits,
        },
        dock_start_end_dock: {
          totalDistance: totalDistanceIncludingDock,
          estimatedTime: totalEstimatedTimeIncludingDock,
        },
      },
      inventory: inventoryTotals,
      job,
      locations,
      fields,
      events,
      disabledStatus,
      startLocation: locations.start,
      endLocation: locations.end,
      customer: job?.users?.find((u) => u.role === JOB_ROLE_MAP.customerRole)?.user,
      month,
      date,
      // Dynamic pricing tier resolved from viewDates forecast for the first timeline date
      dynamicPricing: dynamicPricing ?? undefined,
    };

    return data;
  }
);

// Requirement: `#6517` - Franchise partners the estimator can allocate FP revenue to (the OTR
// split). Anchored at the session zone's corporate/domain ancestor so the list spans all sibling
// franchises (the interstate session zone's own descendants are areas, not the sibling FPs), and
// excludes the interstate zones themselves. Only franchises (type 'franchise') are returned, so
// business-groups (e.g. NSC) drop out while their child franchises remain, flattened.
const INTERSTATE_SPLIT_EXCLUDED_ZONE_IDS = new Set([ 'ymm-interstate-usa', 'ymm-interstate-can' ]);

export function getSplitFranchiseOptions(
  availableZones: BaseZoneWithParentFragment[] | undefined | null,
  sessionZoneId: string | undefined | null,
): Array<{ id: string; name: string }> {
  if (!availableZones?.length || !sessionZoneId) { return []; }

  const branches = generateBranches(availableZones);
  const sessionBranch = branches[sessionZoneId];
  if (!sessionBranch) { return []; }

  // Walk up to the corporate ancestor (fall back to the top-most reachable ancestor).
  let anchor = sessionBranch;
  while (anchor.parent && anchor.zone.type !== 'corporate') {
    anchor = anchor.parent;
  }

  const franchises: Array<{ id: string; name: string }> = [];
  const seen = new Set<string>();
  const stack = [ anchor ];
  while (stack.length) {
    const branch = stack.pop()!;
    if (seen.has(branch.zone.id)) { continue; }
    seen.add(branch.zone.id);

    if (branch.zone.type === 'franchise' && !INTERSTATE_SPLIT_EXCLUDED_ZONE_IDS.has(branch.zone.id)) {
      franchises.push({ id: branch.zone.id, name: branch.zone.name ?? branch.zone.id });
    }

    for (const child of branch.children) { stack.push(child); }
  }

  franchises.sort((a, b) => a.name.localeCompare(b.name));
  return franchises;
}

export const selectSplitFranchises = createSelector(
  brandingFeature.selectAvailableZones,
  zonesFeature.selectCurrentAppZone,
  (availableZones, sessionZone) => getSplitFranchiseOptions(availableZones, sessionZone?.id),
);
